/**
 * AnalyzeWithAIModal
 *
 * Modal per analizzare una chat WhatsApp con Gemini AI.
 * Genera un prompt strutturato per Claude Code.
 */

import { useState, useCallback } from 'react';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { Sparkles, Copy, Check, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { tip } from '@/components/messages/ChatTooltip';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP, AI_DISABLED_MESSAGE, isAiDisabledError, notifyAiDisabled } from '@/lib/hooks/useOrgAiEnabled';

interface AnalyzeWithAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatJid: string;
  configId: string;
  chatName: string;
  selectedMessageIds?: string[]; // Message IDs selected by user
}

interface AnalysisResult {
  prompt: string;
  summary: string;
  media_analyzed: {
    images: number;
    videos: number;
    audio: number;
  };
  messages_analyzed: number;
  remaining_today: number;
}

const MESSAGE_COUNT_OPTIONS = [
  { value: 10, label: 'Ultimi 10 messaggi' },
  { value: 20, label: 'Ultimi 20 messaggi' },
  { value: 50, label: 'Ultimi 50 messaggi' },
  { value: 100, label: 'Ultimi 100 messaggi' },
];

export function AnalyzeWithAIModal({
  isOpen,
  onClose,
  chatJid,
  configId,
  chatName,
  selectedMessageIds,
}: AnalyzeWithAIModalProps) {
  const [messageCount, setMessageCount] = useState(20);
  const [includeMedia, setIncludeMedia] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  // Interruttore IA dell'azienda: spento = pulsante disattivato.
  const aiEnabled = useOrgAiEnabled();

  // Check if we have pre-selected messages
  const hasSelectedMessages = selectedMessageIds && selectedMessageIds.length > 0;

  const handleAnalyze = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Sessione non valida. Effettua nuovamente il login.');
      }

      // Build request body - use selected message IDs if available
      const requestBody: Record<string, unknown> = {
        chat_jid: chatJid,
        config_id: configId,
        include_media: includeMedia,
      };

      if (hasSelectedMessages) {
        requestBody.message_ids = selectedMessageIds;
      } else {
        requestBody.message_count = messageCount;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-whatsapp-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (isAiDisabledError(data)) {
          notifyAiDisabled();
          throw new Error(AI_DISABLED_MESSAGE);
        }
        if (response.status === 429) {
          throw new Error('Hai raggiunto il limite giornaliero di 10 analisi. Riprova domani.');
        }
        throw new Error(data.error || 'Errore durante l\'analisi');
      }

      if (!data.success) {
        throw new Error(data.error || 'Analisi fallita');
      }

      setResult(data);
    } catch (err) {
      console.error('Error analyzing chat:', err);
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setIsLoading(false);
    }
  }, [chatJid, configId, messageCount, includeMedia, hasSelectedMessages, selectedMessageIds]);

  const handleCopy = useCallback(async () => {
    if (!result?.prompt) return;

    try {
      await navigator.clipboard.writeText(result.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [result?.prompt]);

  const handleClose = useCallback(() => {
    setResult(null);
    setError(null);
    setShowOptions(false);
    onClose();
  }, [onClose]);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Analizza con IA"
      size="lg"
    >
      <div className="space-y-4">
        {/* Header con icona */}
        <div className="flex items-center gap-3 pb-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
          <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h4 className="font-medium text-[#37352F] dark:text-white">
              Analisi chat: {chatName}
            </h4>
            <p className="text-xs text-[#787774] dark:text-gray-400">
              Genera un prompt per Claude Code dal contenuto della chat
            </p>
          </div>
        </div>

        {!result ? (
          <>
            {/* Opzioni */}
            <div className="space-y-3">
              {/* Messaggi selezionati o dropdown */}
              {hasSelectedMessages ? (
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    {selectedMessageIds!.length} {selectedMessageIds!.length === 1 ? 'messaggio selezionato' : 'messaggi selezionati'}
                  </p>
                  <p className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-0.5">
                    Verranno analizzati solo i messaggi che hai selezionato
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-[#37352F] dark:text-white mb-1.5">
                    Messaggi da analizzare
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => setShowOptions(!showOptions)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-[#F7F7F5] dark:bg-[#25292E] border border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] rounded-lg text-sm text-[#37352F] dark:text-white hover:bg-[#EFEFEF] dark:hover:bg-[#2C3035] transition-colors"
                    >
                      <span>{MESSAGE_COUNT_OPTIONS.find(o => o.value === messageCount)?.label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${showOptions ? 'rotate-180' : ''}`} />
                    </button>
                    {showOptions && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] rounded-lg shadow-lg overflow-hidden">
                        {MESSAGE_COUNT_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setMessageCount(option.value);
                              setShowOptions(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-[#F7F7F5] dark:hover:bg-[#25292E] transition-colors ${
                              messageCount === option.value
                                ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                                : 'text-[#37352F] dark:text-white'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Checkbox media */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeMedia}
                  onChange={(e) => setIncludeMedia(e.target.checked)}
                  className="w-4 h-4 rounded border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-[#37352F] dark:text-white">
                  Includi media (immagini, video, audio)
                </span>
              </label>

              {/* Info */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  L'analisi utilizzerà Gemini AI per capire il problema segnalato dal cliente
                  e generare un prompt strutturato da usare con Claude Code.
                </p>
              </div>
            </div>

            {/* Errore */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Bottone analizza */}
            <button
              onClick={handleAnalyze}
              disabled={isLoading || !aiEnabled}
              title={!aiEnabled ? AI_DISABLED_TOOLTIP : undefined}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Analisi in corso...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Analizza conversazione</span>
                </>
              )}
            </button>

            {/* Loading message */}
            {isLoading && (
              <p className="text-center text-xs text-[#787774] dark:text-gray-400">
                L'analisi potrebbe richiedere 10-30 secondi...
              </p>
            )}
          </>
        ) : (
          <>
            {/* Risultato */}
            <div className="space-y-3">
              {/* Stats */}
              <div className="flex items-center gap-4 text-xs text-[#787774] dark:text-gray-400">
                <span>{result.messages_analyzed} messaggi analizzati</span>
                {result.media_analyzed.images > 0 && (
                  <span>{result.media_analyzed.images} immagini</span>
                )}
                {result.media_analyzed.videos > 0 && (
                  <span>{result.media_analyzed.videos} video</span>
                )}
                {result.media_analyzed.audio > 0 && (
                  <span>{result.media_analyzed.audio} audio</span>
                )}
              </div>

              {/* Prompt generato */}
              <div className="relative">
                <div className="max-h-[400px] overflow-y-auto p-4 bg-[#F7F7F5] dark:bg-[#25292E] rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                  <pre className="text-sm text-[#37352F] dark:text-white whitespace-pre-wrap font-mono leading-relaxed">
                    {result.prompt}
                  </pre>
                </div>

                {/* Bottone copia floating */}
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 p-2 bg-white dark:bg-[#2F3437] rounded-lg shadow-sm border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] hover:bg-[#F7F7F5] dark:hover:bg-[#25292E] transition-colors"
                  {...tip('Copia prompt')}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-[#787774]" />
                  )}
                </button>
              </div>

              {/* Remaining */}
              <p className="text-xs text-[#787774] dark:text-gray-400 text-center">
                Analisi rimanenti oggi: {result.remaining_today}/10
              </p>

              {/* Azioni */}
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Copiato!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copia prompt</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setResult(null);
                    setError(null);
                  }}
                  className="px-4 py-2 bg-[#F7F7F5] dark:bg-[#25292E] hover:bg-[#EFEFEF] dark:hover:bg-[#2C3035] text-[#37352F] dark:text-white rounded-lg font-medium transition-colors"
                >
                  Nuova analisi
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </ResponsiveModal>
  );
}
